/**
 * Encoding vocabulary (PRD 7.3 and Appendix A), shared by the server and the browser client:
 * the project-type palette, the reserved status colours, the confidence patterns, and the
 * record-status border treatments. Pure data and pure functions, no DOM, so the forecast
 * reads, the exports, and the views all name a colour the same way.
 *
 * Colours themselves live in `public/index.html` as custom properties, one set per theme; this
 * module only decides which slot a value takes and what to call it. Slot choice is deliberate:
 * hues are assigned in a fixed order by project volume, capped at seven plus Other, and never
 * re-assigned when a filter removes a type.
 */

/** The seven type slots, in assignment order, then the neutral Other slot. */
export const TYPE_SLOTS = Object.freeze([
  Object.freeze({ slot: 1, name: 'blue', var: '--type-1' }),
  Object.freeze({ slot: 2, name: 'orange', var: '--type-2' }),
  Object.freeze({ slot: 3, name: 'aqua', var: '--type-3' }),
  Object.freeze({ slot: 4, name: 'violet', var: '--type-4' }),
  Object.freeze({ slot: 5, name: 'yellow', var: '--type-5' }),
  Object.freeze({ slot: 6, name: 'magenta', var: '--type-6' }),
  Object.freeze({ slot: 7, name: 'green', var: '--type-7' }),
]);

export const OTHER_SLOT = Object.freeze({ slot: 8, name: 'other', var: '--type-other' });

/** How many distinct hues a type may claim before the rest fold into Other. */
export const MAX_TYPE_HUES = TYPE_SLOTS.length;

/**
 * The four reserved threshold states (PRD 7.4), keyed as the forecast engine bands them
 * (`utilizationBand` in modules/forecast/core/engine.js), so a cell's band from the API names a
 * colour here directly. Never a project type, never a series. Each carries an icon and a label
 * wherever it is drawn, because green and red cannot be told apart by hue under deuteranopia.
 */
export const STATUS_STATES = Object.freeze([
  Object.freeze({
    key: 'underload',
    label: 'Under-utilized',
    icon: '○',
    var: '--status-underload',
    tint: '--status-underload-tint',
  }),
  Object.freeze({
    key: 'healthy',
    label: 'Healthy',
    icon: '✓',
    var: '--status-healthy',
    tint: '--status-healthy-tint',
  }),
  Object.freeze({
    key: 'caution',
    label: 'Caution',
    icon: '▲',
    var: '--status-caution',
    tint: '--status-caution-tint',
  }),
  Object.freeze({
    key: 'overloaded',
    label: 'Overloaded',
    icon: '■',
    var: '--status-overloaded',
    tint: '--status-overloaded-tint',
  }),
]);

/**
 * Confidence (PRD 6.1, Appendix A): fill pattern plus a marker, never opacity or colour alone.
 * `fill` names the pattern in `patterns.js`.
 */
export const CONFIDENCE_TIERS = Object.freeze([
  Object.freeze({ key: 'high', label: 'High', fill: 'solid', marker: null }),
  Object.freeze({ key: 'medium', label: 'Medium', fill: 'hatch', marker: '≈' }),
  Object.freeze({ key: 'low', label: 'Low', fill: 'dots', marker: '!' }),
]);

/** Record status (PRD Appendix A): border treatment, and the only sanctioned use of opacity. */
export const RECORD_STATUSES = Object.freeze([
  Object.freeze({ key: 'open', label: 'Open / committed', border: 'solid', opacity: 1 }),
  Object.freeze({ key: 'scheduled', label: 'Scheduled', border: 'solid', opacity: 1 }),
  Object.freeze({ key: 'opportunity', label: 'Opportunity', border: 'dashed', opacity: 1 }),
  Object.freeze({ key: 'closed', label: 'Closed / reference', border: 'solid', opacity: 0.45 }),
]);

/**
 * Assign a hue slot to every project type, most work first.
 *
 * The order is by priority, then volume descending, ties broken by label then value so two types
 * with the same count never swap hues between runs. The first seven take the seven hues;
 * everything past them, and any type the caller did not list, reads as Other.
 *
 * `priority` exists so that a type which never enters the forecast — internal, proposal, test —
 * cannot claim a hue ahead of work a planner is actually looking at, however many records it has.
 * Callers pass 1 for the types that draw and 0 for the rest.
 *
 * The input is always the whole portfolio, never the filtered subset: that is what makes the
 * mapping survive a filter (PRD 7.3). Callers filter the legend, not the assignment.
 *
 * @param {Array<{ value: string, label?: string, volume?: number, priority?: number }>} types
 * @returns {Array<{ value: string, label: string, volume: number, slot: number, hue: string, var: string }>}
 */
export function assignTypePalette(types) {
  const rows = (types ?? [])
    .filter((t) => t && typeof t.value === 'string' && t.value.length > 0)
    .map((t) => ({
      value: t.value,
      label: t.label ?? t.value,
      volume: Number.isFinite(t.volume) ? Number(t.volume) : 0,
      priority: Number.isFinite(t.priority) ? Number(t.priority) : 0,
    }));

  // Deduplicate on value, keeping the largest volume seen.
  const byValue = new Map();
  for (const row of rows) {
    const seen = byValue.get(row.value);
    if (!seen || row.volume > seen.volume) byValue.set(row.value, row);
  }

  const sorted = [...byValue.values()].sort(
    (a, b) =>
      b.priority - a.priority ||
      b.volume - a.volume ||
      a.label.localeCompare(b.label) ||
      a.value.localeCompare(b.value),
  );

  return sorted.map((row, i) => {
    const slot = i < MAX_TYPE_HUES ? TYPE_SLOTS[i] : OTHER_SLOT;
    return { ...row, slot: slot.slot, hue: slot.name, var: slot.var };
  });
}

/**
 * Look a type up in an assignment. An unknown type is Other, so a type that appeared after the
 * palette was assigned still draws.
 * @param {ReturnType<typeof assignTypePalette>} assignment
 * @param {string | null | undefined} value
 */
export function typeSlot(assignment, value) {
  const found = (assignment ?? []).find((t) => t.value === value);
  if (found) return found;
  return { value: value ?? null, label: 'Other', volume: 0, ...OTHER_SLOT };
}

/** The CSS colour expression for a type, for inline styles and SVG fills. */
export function typeColor(assignment, value) {
  return `var(${typeSlot(assignment, value).var})`;
}

/** The CSS colour expression for a threshold state. */
export function statusColor(key) {
  const found = STATUS_STATES.find((s) => s.key === key);
  return found ? `var(${found.var})` : 'var(--ink-muted)';
}
